/**
 * Top-of-page popup for plan-limit / locked-feature errors (Stage C).
 * Mounted once in AppLayout; anything fires it via showUpgradeToast().
 * Shows the backend's friendly message + a "See plans" button that
 * opens the plans/upgrade modal (deep-linked to the feature).
 */
import React, { useEffect, useRef, useState } from 'react';
import { UPGRADE_TOAST_EVENT, openPlansModal, isQuotaCapCode } from '../utils/plansModal';

export default function UpgradeToast() {
  const [toast, setToast] = useState(null); // { message, feature }
  const timerRef = useRef(null);

  useEffect(() => {
    const onUpgrade = (e) => {
      // A monthly quota CAP opens the PlanLimitOverlay (centrally, from
      // lib/api.js / lib/quota.js) — this toast only handles gated features.
      if (isQuotaCapCode(e.detail?.code)) return;
      setToast({
        message: e.detail?.message || 'This needs a bigger plan.',
        feature: e.detail?.feature || null,
      });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setToast(null), 10000);
    };
    window.addEventListener(UPGRADE_TOAST_EVENT, onUpgrade);
    return () => {
      window.removeEventListener(UPGRADE_TOAST_EVENT, onUpgrade);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!toast) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 18,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10500,
        maxWidth: 560,
        width: 'calc(100% - 32px)',
        background: 'white',
        border: '1px solid #e4e0f7',
        borderRadius: 14,
        boxShadow: '0 12px 34px rgba(30, 20, 80, 0.18)',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        animation: 'au-toast-in 0.25s ease',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 34, height: 34, flexShrink: 0, borderRadius: 10,
          background: 'linear-gradient(135deg, #6B4EE8 0%, #4A90E2 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
        }}
      >
        ⭐
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5, color: '#1a1a2e', marginBottom: 2 }}>
          Plan limit
        </div>
        <div style={{ fontSize: 13, color: '#444', lineHeight: 1.45 }}>{toast.message}</div>
        <button
          type="button"
          onClick={() => { setToast(null); openPlansModal({ highlight: toast.feature }); }}
          style={{
            marginTop: 10, border: 'none', borderRadius: 9, padding: '8px 16px',
            fontSize: 13, fontWeight: 700, cursor: 'pointer', color: 'white',
            background: 'linear-gradient(135deg, #6B4EE8 0%, #4A90E2 100%)',
            boxShadow: '0 2px 8px rgba(107,78,232,0.28)',
          }}
        >
          See plans
        </button>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setToast(null)}
        style={{
          border: 'none', background: 'transparent', cursor: 'pointer',
          fontSize: 14, color: '#888', padding: 2, lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}
